// @flow
import * as React from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import Home from '../Home'
import Mouse from '../Mouse'
import Loader from '../Loader'

import { FireBaseApp } from '../../utils/firebaseUtils'
import ThreeApp from '../../utils/threeApp'
import DailyItemsApp from '../../utils/dailyItemsApp.js'
import PhotographersApp from '../../utils/photographersApp.js'
import MouseStates from '../../utils/MouseStates.js'

import type firebase from 'firebase'
import type THREE from 'three'

import './styles.css'

// arbitrary valu that decides how grow will the loading bar be before we start loading planes
const DATABASE_PERCENT = 30
const TEXTURE_PERCENT = 60
const PLANE_PERCENT = 10

type StateType = {
  DailyItemsApp: ?DailyItemsApp,
  PhotographersApp: ?PhotographersApp,
  MouseStates: MouseStates,  
  textures: ?THREE.Texture,
  percent: number,
  loaderVisible: boolean,
  planesCreated: Array<string>,
  canAnimate: boolean,
  triggerFirstAppearance: boolean
}

class App extends React.Component<{}, StateType> {
  firebaseApp: FireBaseApp

  constructor() {
    super()
    this.firebaseApp = new FireBaseApp()
    this.state = {
      DailyItemsApp: undefined,
      PhotographersApp: undefined,
      MouseStates: new MouseStates(),
      textures: undefined,
      percent: 0,
      loaderVisible: true,
      planesCreated: [],
      canAnimate: false,
      triggerFirstAppearance: false
    }
  }

  componentDidMount () {
    // this.firebaseApp.getDailyItemsValue().then(this.fewDailyItemsLoaded)
    this.firebaseApp.getDailyItemsValue().then(this.dailyItemsLoaded)
    this.firebaseApp.getPhotographersValue().then(this.photographersLoaded)
  }

  componentDidUpdate (prevProps: {}, prevState: StateType) {
    const dailyItemsApp = this.state.DailyItemsApp
    const photographersApp = this.state.PhotographersApp
    const prevDailyItemsApp = prevState.DailyItemsApp
    const prevPhotographersApp = prevState.PhotographersApp    
    if (this.firebaseLoaded(dailyItemsApp, photographersApp) && !this.firebaseLoaded(prevDailyItemsApp, prevPhotographersApp)) this.loadTextures(dailyItemsApp)
  }

  // DEBUG FUNCTION TO AVOID LOADING ALL ARTICLES
  fewDailyItemsLoaded = (data: firebase.database.DataSnapshot) => {
    const dailyItems = data.val()
    const keys = Object.keys(dailyItems).splice(0, 10)
    const truncatedDailyItems = {}
    keys.forEach(key => { truncatedDailyItems[key] = dailyItems[key] })
    const dailyItemsApp = new DailyItemsApp(truncatedDailyItems)
    this.setState((prevState: StateType) => ({
      percent: prevState.percent + DATABASE_PERCENT / 2,
      DailyItemsApp: dailyItemsApp
    }))
  }

  dailyItemsLoaded = (data: firebase.database.DataSnapshot) => {
    const dailyItems = data.val()
    const dailyItemsApp = new DailyItemsApp(dailyItems)
    this.setState((prevState: StateType) => ({
      percent: prevState.percent + DATABASE_PERCENT / 2,
      DailyItemsApp: dailyItemsApp
    }))
  }

  photographersLoaded = (data: firebase.database.DataSnapshot) => {
    const photographers = data.val()
    const photographersApp = new PhotographersApp(photographers)
    this.setState((prevState: StateType) => ({
      percent: prevState.percent + DATABASE_PERCENT / 2,
      PhotographersApp: photographersApp
    }))
  }

  loadTextures = (dailyItemsApp: DailyItemsApp) => {
    const photoPromises = dailyItemsApp.keys.map((key: string) =>
      new Promise((resolve, reject) => {
        ThreeApp.loadPowerOfTwoTexture(dailyItemsApp.dailyItems[key].picture, true)
          .then((texture: THREE.Texture) => {
            this.setState((prevState: StateType) => ({
              percent: this.getTexturePercent(dailyItemsApp, prevState.percent)
            }))
            resolve(texture)
          })
          .catch(() => {
            ThreeApp.loadPowerOfTwoTexture(`/covers/${dailyItemsApp.dailyItems[key].uid}.jpg`, false)
            .then((texture: THREE.Texture) => {
              this.setState((prevState: StateType) => ({
                percent: this.getTexturePercent(dailyItemsApp, prevState.percent)
              }))
              resolve(texture)
            })
            .catch(reject)
          })
      })
    )
    Promise.all(photoPromises).then((textures) => {
      this.setState({ textures })
    })
  }

  firebaseLoaded = (dailyItemsApp: ?DailyItemsApp, photographersApp: ?PhotographersApp): boolean => dailyItemsApp !== undefined && photographersApp !== undefined

  hideLoader = (firstAppearance: boolean, animationDuration: number) => {
    if (firstAppearance) this.setState({ triggerFirstAppearance: true })
    setTimeout(() => {
      this.setState({ loaderVisible: false })
    }, animationDuration);
  }

  setCanAnimate = (canAnimate: boolean) => { this.setState({ canAnimate }) }

  getTexturePercent = (dailyItemsApp: DailyItemsApp, currentPercent: number): number =>
    Math.floor(currentPercent + (TEXTURE_PERCENT / Math.floor(dailyItemsApp.keys.length)))

  getPlanePercent = (planesCreated: Array<string>, dailyItemsApp: DailyItemsApp, currentPercent: number): number => {
    if (dailyItemsApp.keys.length === planesCreated.keys.length) return 100
    const percent = Math.floor(currentPercent + Math.floor(PLANE_PERCENT / dailyItemsApp.keys.length))
    return percent >= 100 ? 100 : currentPercent + percent
  }

  setPlaneCreated = (uid: string) => {
    const dailyItemsApp = this.state.DailyItemsApp
    if (!dailyItemsApp) return undefined
    this.setState((prevState: StateType) => {
      const planesCreated = [...prevState.planesCreated, uid]
      if (planesCreated.length === dailyItemsApp.keys.length) return { planesCreated, canAnimate: true, percent: this.getPlanePercent(planesCreated, dailyItemsApp, prevState.percent) }
      else return { planesCreated, percent: this.getPlanePercent(planesCreated, dailyItemsApp, prevState.percent) }
    })
  }

  render() {
    const dailyItemsApp = this.state.DailyItemsApp
    const photographersApp = this.state.PhotographersApp
    return(
      <Router>
        <Route path="/" render={props => (
          <div className="app-container">
            <Mouse isFirstPlane={false} MouseStates={this.state.MouseStates} />
            <Loader
              percent={this.state.percent}
              hideLoader={this.hideLoader}
              visible={this.state.loaderVisible}
              setCanAnimate={this.setCanAnimate}
            />
            {dailyItemsApp && photographersApp && this.state.textures &&
              <Home
                {...props}
                firebaseApp={this.firebaseApp}
                DailyItemsApp={dailyItemsApp}
                PhotographersApp={photographersApp}
                MouseStates={this.state.MouseStates}
                textures={this.state.textures}
                canAnimate={this.state.canAnimate}
                setPlaneCreated={this.setPlaneCreated}
                triggerFirstAppearance={this.state.triggerFirstAppearance}
              />            
            }
          </div>
        )}/>
      </Router>
    )
  }
}

export default App


// WEBPACK FOOTER //
// ./src/components/App/index.jsx